import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import { Save, Edit } from '@mui/icons-material';

const TokenTypeForm = ({ type, label, icon, color, settings, defaultSettings, onSave }) => {
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);
  // console.log("Form Data => ", defaultSettings)
  useEffect(() => {
    if (settings) {
      setFormData(settings);
    } else {
      setFormData(defaultSettings);
    }
  }, [settings, defaultSettings]);

  const handleInputChange = (field) => (event) => {
    const value = field === 'prefix'
      ? event.target.value.toUpperCase().replace(/[^A-Z]/g, '')
      : parseInt(event.target.value) || 0;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    setSaving(true);

    // Simulate API call
    setTimeout(() => {
      onSave(type, formData);
      setEditMode(false);
      setSaving(false);
    }, 500);
  };

  const handleCancel = () => {
    setEditMode(false);
    if (settings) {
      setFormData(settings);
    } else {
      setFormData(defaultSettings);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ fontSize: '2rem' }}>{icon}</Typography>
          <Box>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: '1.5rem' }}>
              {label} Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
              Configure {label.toLowerCase()} format
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {!editMode ? (
            <Button
              variant="contained"
              startIcon={<Edit sx={{ fontSize: '12px' }} />}
              onClick={handleEdit}
              color={color}
              sx={{ fontSize: '12px' }}
            >
              {settings ? 'Edit Settings' : 'Create Settings'}
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<Save sx={{ fontSize: '12px' }} />}
                onClick={handleSave}
                disabled={saving}
                color="success"
                sx={{ fontSize: '12px' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                sx={{ fontSize: '12px' }}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Form Card */}
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {/* Prefix Field */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                  Token Prefix
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px', mb: 2 }}>
                  Letters that appear before the number
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g., DLV"
                  value={formData?.prefix}
                  onChange={handleInputChange('prefix')}
                  disabled={!editMode}
                  size="small"
                  sx={{ fontSize: '12px' }}
                  inputProps={{
                    maxLength: 5,
                    style: { fontSize: '12px' }
                  }}
                />
              </Box>
            </Grid>

            {/* Start Number Field */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                  Start Number
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px', mb: 2 }}>
                  Starting number for token sequence
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g., 1"
                  value={formData?.start_number}
                  onChange={handleInputChange('start_number')}
                  disabled={!editMode}
                  size="small"
                  sx={{ fontSize: '12px' }}
                  inputProps={{
                    min: 1,
                    style: { fontSize: '12px' }
                  }}
                />
              </Box>
            </Grid>

            {/* Minimum Digits Field */}
            <Grid item xs={12} md={4}>
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '12px', fontWeight: 'bold' }}>
                  Number of Digits
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px', mb: 2 }}>
                  Minimum digits for the number (zero-padded)
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  placeholder="e.g., 3"
                  value={formData?.numbers}
                  onChange={handleInputChange('numbers')}
                  disabled={!editMode}
                  size="small"
                  sx={{ fontSize: '12px' }}
                  inputProps={{
                    min: 1,
                    max: 6,
                    style: { fontSize: '12px' }
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Format Preview */}
          {/* <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '12px', fontWeight: 'bold' }}>
              Token Format Preview
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '11px', mb: 1 }}>
                Token format: <code>{`{PREFIX}{NUMBER}`}</code>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <Typography variant="body2" sx={{ fontSize: '12px' }}>
                  Example:
                </Typography>
                <Chip
                  label={`${formData?.prefix}${String(formData?.start_number).padStart(formData?.min_digits, '0')}`}
                  color={color}
                  variant="outlined"
                  sx={{ fontSize: '12px', fontWeight: 'bold' }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px', ml: 'auto' }}>
                  e.g., DLV001, TAK001, etc.
                </Typography>
              </Box>
            </Box>
          </Box> */}
        </CardContent>
      </Card>

      {/* Status */}
      {/* <Box sx={{ mt: 3 }}>
        {formData.reflected_at ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label="Settings Saved"
              color="success"
              variant="outlined"
              size="small"
              sx={{ fontSize: '11px' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
              Last updated: {
                formData.reflected_at
                  ? `${String(new Date(formData.reflected_at).getDate()).padStart(2, '0')}/${String(new Date(formData.reflected_at).getMonth() + 1).padStart(2, '0')
                  }/${new Date(formData.reflected_at).getFullYear()}`
                  : 'Never'
              }
            </Typography>
          </Box>
        ) : (
          <Chip
            label="No Settings Configured"
            color="default"
            variant="outlined"
            size="small"
            sx={{ fontSize: '11px' }}
          />
        )}
      </Box> */}
    </Box>
  );
};

export default TokenTypeForm;